import { isPlaceholderAddress, addressLocality, unavailableAddressLabel } from './addressQuality';

function isPortalOnlyRecord(da) {
  const number = `${da?.da_number || ''}`.toUpperCase();
  const sourceId = `${da?.source_id || da?.source || da?.data_source || ''}`.toLowerCase();
  return number.startsWith('PAN-') || number.startsWith('CDC-') || sourceId.includes('portal') || sourceId.startsWith('cdc_');
}

const STREET_ABBREVS = [
  [/\bav\b|\bave\b/gi, 'avenue'],
  [/\bbvd\b|\bblvd\b/gi, 'boulevard'],
  [/\bcct\b|\bcir\b/gi, 'circuit'],
  [/\bcl\b/gi, 'close'],
  [/\bct\b/gi, 'court'],
  [/\bcr\b|\bcres\b/gi, 'crescent'],
  [/\bdr\b/gi, 'drive'],
  [/\besp\b/gi, 'esplanade'],
  [/\bgr\b/gi, 'grove'],
  [/\bhwy\b/gi, 'highway'],
  [/\bln\b/gi, 'lane'],
  [/\bpde\b/gi, 'parade'],
  [/\bpl\b/gi, 'place'],
  [/\brd\b/gi, 'road'],
  [/\bst\b/gi, 'street'],
  [/\btce\b/gi, 'terrace'],
];

function normaliseAddressKey(address) {
  let out = String(address || '').toLowerCase();
  out = out.replace(/\bnsw\b/g, ' ');
  out = out.replace(/\b\d{4}\b/g, ' ');
  for (const [pattern, replacement] of STREET_ABBREVS) out = out.replace(pattern, replacement);
  return out.replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function datePart(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) return null;
  return parsed.toISOString().slice(0, 10);
}

function normalisedPortalRef(da) {
  const ref = da?.planning_portal_app_number || da?.pan_number || da?.da_number || '';
  const clean = String(ref).trim().toUpperCase();
  return /^(PAN|CDC)-/.test(clean) ? clean : null;
}

export function displayDAIdentifier(da, fallback = 'DA') {
  const councilRef = String(da?.council_application_number || '').trim();
  if (councilRef) return councilRef;

  const daNumber = String(da?.da_number || '').trim();
  if (daNumber && !/^(PAN|CDC)-/i.test(daNumber)) return daNumber;

  const portalRef = String(da?.planning_portal_app_number || da?.pan_number || '').trim();
  if (portalRef) return portalRef;

  return daNumber || fallback;
}

export function isParcelOnlyAddress(address) {
  const raw = String(address || '').trim();
  if (!raw) return false;
  if (/^\s*\d+[a-z]?\s*\/\s*(?:\d+[a-z]?\s*)?\/\s*(?:(?:dp|sp)\s*)?\d+[a-z]?\s*$/i.test(raw)) {
    return true;
  }

  const words = raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (words.length < 2) return false;

  const hasLot = words.some(word => word === 'lot' || word === 'lots' || /^l\d+[a-z]?$/.test(word));
  const hasPlan = words.some(word => /^(dp|sp)\d*[a-z]?$/.test(word) || word === 'section' || word === 'sec');
  if (!hasLot || !hasPlan) return false;

  return words.every(word =>
    word === 'lot' ||
    word === 'lots' ||
    word === 'and' ||
    word === 'in' ||
    word === 'part' ||
    word === 'of' ||
    word === 'strata' ||
    word === 'scheme' ||
    word === 'section' ||
    word === 'sec' ||
    /^(dp|sp)\d*[a-z]?$/.test(word) ||
    /^[0-9]+[a-z]?$/.test(word) ||
    /^l\d+[a-z]?$/.test(word)
  );
}

export function displayDAAddress(da, fallback = '') {
  const address = String(da?.address || '').trim();
  if (address && !isParcelOnlyAddress(address) && !isPlaceholderAddress(address)) return address;
  return String(fallback || '').trim();
}

// A presentation label, never an address for matching or geocoding.
export function displayDALocation(da, fallback = '') {
  if (isPlaceholderAddress(da?.address)) return [unavailableAddressLabel, addressLocality(da)].filter(Boolean).join(', ');
  return displayDAAddress(da, fallback);
}

export function isEarlyPortalSignal(da) {
  return isPortalOnlyRecord(da) && !da?.source_url;
}

export function getDAFeedSortTime(da) {
  if (isEarlyPortalSignal(da) && da?.created_at) {
    const createdTime = new Date(da.created_at).getTime();
    if (Number.isFinite(createdTime)) return createdTime;
  }

  if (da?.lodged_date) {
    const lodgedTime = new Date(da.lodged_date).getTime();
    return Number.isFinite(lodgedTime) ? lodgedTime : 0;
  }

  const createdTime = da?.created_at ? new Date(da.created_at).getTime() : 0;
  if (!Number.isFinite(createdTime)) return 0;

  const time = createdTime;
  return Number.isFinite(time) ? time : 0;
}

export function sortDAsForFeed(rows) {
  return [...(rows || [])].sort((a, b) => {
    const byFreshness = getDAFeedSortTime(b) - getDAFeedSortTime(a);
    if (byFreshness !== 0) return byFreshness;

    const byCreated = new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime();
    if (byCreated !== 0) return byCreated;

    const costA = Number(a?.estimated_cost || a?.cost_of_development || 0);
    const costB = Number(b?.estimated_cost || b?.cost_of_development || 0);
    return costB - costA;
  });
}

function displayDuplicateKeys(da) {
  const keys = [];
  const council = String(da?.council || '').trim().toLowerCase();
  const lodged = datePart(da?.lodged_date);
  const portalRef = normalisedPortalRef(da);
  const address = normaliseAddressKey(da?.address);

  if (council && portalRef) {
    keys.push(`portal-ref|${council}|${portalRef}`);
  }

  if (council && lodged && address && !isParcelOnlyAddress(da?.address) && !isPlaceholderAddress(da?.address)) {
    keys.push(`address-date|${council}|${lodged}|${address}`);
  }

  const cost = Number(da?.estimated_cost || da?.cost_of_development || 0);
  if (!isPlaceholderAddress(da?.address) && council && lodged && Number.isFinite(cost) && cost > 0) {
    keys.push(`cost-date|${council}|${lodged}|${Math.round(cost)}`);
  }

  return keys;
}

function displayPriority(da) {
  let score = 0;
  if (da?.source_url) score += 20;
  if (!isPortalOnlyRecord(da)) score += 10;
  if (da?.council_application_number) score += 5;
  if (da?.da_number && !String(da.da_number).toUpperCase().startsWith('PAN-')) score += 3;
  return score;
}

function mergeDisplayRecord(primary, duplicate) {
  return {
    ...duplicate,
    ...primary,
    planning_portal_app_number: primary.planning_portal_app_number || duplicate.planning_portal_app_number,
    council_application_number: primary.council_application_number || duplicate.council_application_number,
    latitude: primary.latitude ?? duplicate.latitude,
    longitude: primary.longitude ?? duplicate.longitude,
    estimated_cost: primary.estimated_cost ?? duplicate.estimated_cost,
    cost_of_development: primary.cost_of_development ?? duplicate.cost_of_development,
  };
}

function likelyCrossSourceDuplicate(a, b) {
  const aPortal = isPortalOnlyRecord(a);
  const bPortal = isPortalOnlyRecord(b);
  return aPortal !== bPortal ||
    ((aPortal || bPortal) && Boolean(a?.source_url) !== Boolean(b?.source_url));
}

function addEntryToIndexes(indexes, entry, keys) {
  for (const key of keys) {
    if (!indexes.has(key)) indexes.set(key, []);
    indexes.get(key).push(entry);
  }
}

export function dedupeDAsForDisplay(rows) {
  const output = [];
  const grouped = new Map();

  for (const row of rows || []) {
    const keys = displayDuplicateKeys(row);
    if (!keys.length) {
      output.push(row);
      continue;
    }

    const candidates = [...new Set(keys.flatMap(key => grouped.get(key) || []))];
    const existing = candidates.find(candidate => likelyCrossSourceDuplicate(candidate.row, row));

    if (!existing) {
      addEntryToIndexes(grouped, { index: output.length, row }, keys);
      output.push(row);
      continue;
    }

    const keepCurrent = displayPriority(row) > displayPriority(existing.row);
    const primary = keepCurrent ? row : existing.row;
    const duplicate = keepCurrent ? existing.row : row;
    const merged = mergeDisplayRecord(primary, duplicate);
    output[existing.index] = merged;
    existing.row = merged;
    addEntryToIndexes(grouped, existing, displayDuplicateKeys(merged));
  }

  return output;
}

export { isPortalOnlyRecord };

export function relatedAddressGroups(rows = []) {
  const groups = new Map();
  for (const row of rows) {
    if (!row?.address || isParcelOnlyAddress(row.address) || isPlaceholderAddress(row.address)) continue;
    const key = `${row.council || ''}|${normaliseAddressKey(row.address)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  }
  return [...groups.values()].filter(group => group.length > 1);
}
